import { cn, TimeSlotSuggestionType } from "@repo/lib";
import {
  createContext,
  useContext,
  type HTMLAttributes,
  type InputHTMLAttributes,
  useState,
  forwardRef,
  useMemo,
  useCallback,
} from "react";

interface AvailableOptionsSelectorRootProps {
  children: React.ReactElement<AvailableOptionsSelectorListProps>;
  name: string;
  onChange?: (value: string) => void;
}

export const AvailableOptionsSelectorRoot = ({
  children,
  name,
  onChange,
}: AvailableOptionsSelectorRootProps): JSX.Element => {
  const [checkedValue, setCheckedValue] = useState("");

  const handleOnChange = useCallback(
    (value: string): void => {
      setCheckedValue(value);

      onChange?.(value);
    },
    [onChange],
  );

  const contextValue = useMemo(
    () => ({ name, onChange: handleOnChange, checkedValue }),
    [name, handleOnChange, checkedValue],
  );

  return (
    <AvailableOptionsSelectorContext.Provider value={contextValue}>
      <div className="flex flex-col gap-1">{children}</div>
    </AvailableOptionsSelectorContext.Provider>
  );
};

interface AvailableOptionsSelectorListProps extends HTMLAttributes<HTMLDivElement> {
  children:
    | React.ReactElement<AvailableOptionsSelectorItemProps>[]
    | React.ReactElement<AvailableOptionsSelectorItemProps>;
}

export const AvailableOptionsSelectorList = ({
  children,
}: AvailableOptionsSelectorListProps): JSX.Element => {
  return <div className="bg-white rounded-30 flex flex-col gap-3">{children}</div>;
};

interface AvailableOptionsSelectorItemProps
  extends InputHTMLAttributes<HTMLInputElement> {
  isChecked: boolean;
  data: { acronym?: string; timeSlot: string; timeSlotType?: TimeSlotSuggestionType };
  displayName: string;
  children: React.ReactNode;
  className?: string;
}

export const AvailableOptionsSelectorItem = forwardRef<
  HTMLInputElement,
  AvailableOptionsSelectorItemProps
>(
  (
    { isChecked, data, displayName, children, className, ...props },
    ref: React.Ref<HTMLInputElement>,
  ): JSX.Element => {
    const { name, onChange } = useAvailableOptionsSelectorContext();

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      onChange?.(e.target.value, e.target.checked);
    };

    return (
      <label
        className={cn(
          "relative flex flex-row items-center border border-neutral-200 py-4 px-6 gap-4 cursor-pointer group rounded-lg",
          isChecked && "border-secondary-200",
          className,
        )}
        htmlFor={props.id}
        aria-label={`Clique para selecionar a unidade ${displayName} no horário ${data.timeSlot}`}
      >
        <span className="group-has-[:checked]:text-neutral-600 text-neutral-500 font-medium w-full">
          <div className="flex flex-col w-full justify-center items-stretch">
            <div className="flex flex-row justify-between items-start text-start">
              <div className="flex flex-row">
                <div className="relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    className="appearance-none items-center justify-center focus:outline-none"
                    name={name}
                    onChange={handleOnChange}
                    checked={isChecked}
                    ref={ref}
                    {...props}
                  />
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group group-has-[:checked]:bg-white group-has-[:checked]:w-[6px] group-has-[:checked]:h-[6px] group-has-[:checked]:rounded-lg" />
                </div>
                <div className="flex flex-col gap-1">
                  <div className={cn("text-neutral-600 font-medium text-md")}>
                    {displayName}
                  </div>
                  <div className={cn("text-neutral-600 font-semibold text-2xl")}>
                    {data.timeSlot}
                  </div>
                </div>
              </div>

              {data.acronym ? (
                <div
                  className={cn(
                    "flex flex-row items-center justify-center text-primary font-medium text-xs bg-neutral-100 py-1 px-2 rounded-xl",
                    {
                      "bg-white":
                        data.timeSlotType ===
                        TimeSlotSuggestionType.LimitedDurationAppointment,
                    },
                  )}
                >
                  {data.acronym}
                </div>
              ) : null}
            </div>
            <div className="flex flex-col gap-1 mt-2">{children}</div>
          </div>
        </span>
      </label>
    );
  },
);

AvailableOptionsSelectorItem.displayName = "AvailableOptionsSelectorItem";

interface AvailableOptionsSelectorContextProps {
  name?: string;
  checkedValue?: string;
  onChange?: (value: string, isChecked: boolean) => void;
}

const AvailableOptionsSelectorContext =
  createContext<AvailableOptionsSelectorContextProps>({});

const useAvailableOptionsSelectorContext = (): AvailableOptionsSelectorContextProps =>
  useContext(AvailableOptionsSelectorContext);
